/**
 * 登录态与**登录挂起/恢复**。
 *
 * 方针是「**登录前置**」(2026-09-02 产品拍板走方案 A)：打开直接看到聊天窗口，
 * 但**第一次发送时**就要求登录 —— 不是「打开就登录」，也不是「先用后登录」。
 * 登录完成后**不自动发送**，把焦点还给输入框，用户自己再点一次(产品第 3 点)。
 *
 * ⚠⚠ 本文件不再领匿名令牌。`/api/auth/anonymous` 端点与影子合并/归属改判在服务端
 *   仍然保留(将来放开「先逛后登录」还要用)，只是 v1 前端不调 —— 详见
 *   《登录前置改造-开发与验收计划-20260902》BE-3。
 *
 * ⚠ 两个入口共用这一套机制：
 *   1. 发送拦截 —— 未登录时点发送(见 chat-store 的 `send`)；
 *   2. 常驻 —— 界面上的「登录」按钮。
 *   分成两套的下场是其中一套忘了恢复动作，而那种 bug 只在真人操作时才现形。
 */
import { create } from 'zustand'
import { isApiError, singleFlight } from '@cione/api-client'
import type { SmsScene, User } from '@cione/contracts'
import { api, setAuthLostHandler } from '@/lib/api'
import { queryClient } from '@/lib/query-client'

/** 被挂起的动作。登录成功后原地执行。 */
export interface PendingAction {
  /** 给界面显示提示，让用户知道自己没有白点。 */
  label: string
  /**
   * 提示文案的 i18n 键。不给就用「登录后将继续：{{label}}」。
   *
   * ⚠ 发送拦截那个入口必须传 `login.draftKeptHint`：那里登录后**不会**自动继续，
   *   用默认文案等于在界面上承诺一件我们不做的事。
   */
  hintKey?: string
  run: () => void | Promise<void>
}

export type LoginMethod = 'sms' | 'password'

/**
 * 换账号(登出 / 换成另一个号登录)时要清场的一方自己挂进来。
 *
 * ⚠ 用「留口子」而不是直接 import chat-store：chat-store 已经 import 了本文件，
 *   反向再 import 就成环。这与 `lib/api.ts` 的 `setAuthLostHandler` 是同一个套路。
 */
let accountChangedHandler: (() => void) | null = null

export function setAccountChangedHandler(handler: () => void): void {
  accountChangedHandler = handler
}

/**
 * 本会话最近一次见过的 userId。
 *
 * ⚠ 只用来识别「换了另一个账号」。不能拿 state 里的 `user` 判 ——
 *   令牌失效那条路径会先把 `user` 清成 null，等真正登录时就分不出是换号还是重登了。
 */
let lastKnownUserId: string | null = null

interface AuthState {
  /** 首屏是否已经完成「恢复登录态」。false 时界面显示占位。 */
  ready: boolean
  user: User | null
  loginOpen: boolean
  pending: PendingAction | null

  boot: () => Promise<void>
  /** 需要登录的地方一律调这个。已登录就直接执行动作，未登录就挂起 + 弹登录。 */
  requireLogin: (action?: PendingAction) => void
  /**
   * 服务端在**业务请求**上明确回了 `AUTH.*` —— 这个身份不作数了。
   *
   * ⚠⚠ 必须先把本地登录态清掉再弹登录：`requireLogin` 看见 `user` 还是 USER
   *   会以为「已登录」而直接执行恢复动作，**登录框根本不弹** ——
   *   用户卡在一个「点发送没反应」的界面上，这是最难自查的一类现象。
   *   (`AUTH.TOKEN_INVALID` 不在 http 层的可刷新码里，所以 `onAuthLost` 不会替我们
   *    清登录态；那条路径只覆盖「刷新也救不回来」的情形。)
   *
   * ⚠ **不动令牌**：`AUTH.ACTION_FORBIDDEN` 时令牌完全有效，只是等级不够 ——
   *   清了反而把一个好令牌扔了。令牌的生死只由 http 层的单飞刷新决定。
   */
  sessionRejected: (action?: PendingAction) => void
  cancelLogin: () => void
  /**
   * 发码。⚠ `scene` 必须给对：登录用 `login`，改密用 `bind` ——
   * 服务端按 scene 分键存码，传错的话码存进一个永远对不上的键，
   * 用户等一条不会到的短信，而界面上什么错都看不见。
   */
  sendCode: (phone: string, scene?: SmsScene) => Promise<number>
  login: (input: { phone: string; smsCode?: string; password?: string }) => Promise<void>
  logout: () => Promise<void>
  updateNickname: (nickname: string) => Promise<void>
  setPassword: (input: { smsCode: string; newPassword: string }) => Promise<void>
}

export const useAuthStore = create<AuthState>()((set, get) => {
  /**
   * 首屏恢复登录态。
   *
   * ⚠⚠ **没有令牌就一个请求都不发**(FE-1)：产品第 1 点「打开直接看到聊天窗口」
   *   的真正落点在这里。方案 B 时代首屏必然有一次网络往返(有令牌等 A7、
   *   无令牌等 A1)，`AuthGate` 在这期间整屏显示「加载中」—— 断网时更是要等超时。
   *   方案 A 下未登录用户不需要任何身份，首屏就该是同步的(见下面 `ready` 的初值)。
   *
   * ⚠ 外面这层 `singleFlight` 保留：StrictMode 在 dev 下会把 effect 跑两遍，
   *   有令牌时会打两次 `/api/auth/me`。
   */
  const boot = singleFlight(async (): Promise<void> => {
    if (!api.tokenStore.get()) {
      // 未登录。⚠ 这一支【零请求】—— 断网也照样立刻出界面
      set({ user: null, ready: true })
      return
    }
    try {
      // 有令牌先问「我是谁」—— 比信本地缓存的 user 可靠(等级可能被后台改过)
      const user = await api.auth.me()
      lastKnownUserId = user.userId
      set({ user, ready: true })
    } catch (error) {
      // ⚠⚠ 只有【鉴权类】失败才清令牌：断网/网关没起时 A7 也会失败，
      //   那时清掉等于把一个还有效的 refresh 令牌扔了 —— 用户回到有网的地方
      //   发现自己被登出了，而他什么都没做。
      //   ⚠ 无论哪种失败都**不重领匿名令牌**(方案 A)，也不重试第二次。
      if (isApiError(error) && error.code.startsWith('AUTH.')) api.tokenStore.set(null)
      set({ user: null, ready: true })
    }
  })

  /**
   * 令牌彻底失效(refresh 也救不回来)。
   *
   * ⚠ 清登录态 → 弹登录，**中间不再领匿名令牌**(FE-2)。
   *   ⚠ 这里刻意不挂恢复动作：真正知道「用户刚才想干什么」的是 chat-store，
   *     它在自己的 catch 里会再调一次 `requireLogin` 把带草稿的恢复动作挂上
   *     (顺序上它在后面，会覆盖这一次的空 pending)。
   */
  setAuthLostHandler(() => {
    set({ user: null })
    get().requireLogin()
  })

  return {
    // ⚠ 无令牌时**初值就是 true**：一次渲染都不闪「加载中」。
    //   有令牌才需要等 A7 回来 —— 那一屏本来就要显示用户信息。
    ready: api.tokenStore.get() === null,
    user: null,
    loginOpen: false,
    pending: null,

    boot,

    requireLogin(action) {
      const level = get().user?.level
      if (level === 'USER' || level === 'ADMIN') {
        void action?.run()
        return
      }
      set({ pending: action ?? null, loginOpen: true })
    },

    sessionRejected(action) {
      set({ user: null })
      get().requireLogin(action)
    },

    cancelLogin() {
      // ⚠ 只关面板、丢挂起动作。草稿不归这里管，它在 chat-store 里原样留着 ——
      //   用户点「以后再说」之后再点发送，还能重新弹一次登录。
      set({ loginOpen: false, pending: null })
    },

    async sendCode(phone, scene = 'login') {
      const { cooldownSeconds } = await api.auth.sendCode({ countryCode: '86', phone, scene })
      return cooldownSeconds
    },

    async login(input) {
      // ⚠ 仍然走 auth: 'optional'(api-client 里)：方案 A 下本地没有匿名令牌可带，
      //   所以「合并影子用户」这段逻辑自然不会被触发 —— 保留无害，将来放开匿名还要用。
      const result = await api.auth.login({
        countryCode: '86',
        phone: input.phone,
        ...(input.smsCode === undefined ? {} : { smsCode: input.smsCode }),
        ...(input.password === undefined ? {} : { password: input.password }),
      })

      // ⚠ 按 userId 缓存过的东西必须作废：登录前后的 userId 不是同一个人
      queryClient.clear()

      /**
       * ⚠⚠ 换了**另一个**账号才清屏换会话 id。
       *
       *   Java 侧记忆按 `user_id` 隔离，A 的会话 id 在 B 名下只会读到空窗口，
       *   而屏幕上还留着 A 的对话 —— 既像「AI 突然失忆」，也是隐私问题。
       *   ⚠ 同一个账号重新登录(令牌失效后重登)**不能**清：那会把用户整段对话
       *     从屏幕上抹掉，而服务端记忆其实还在。
       *   ⚠ 首次登录(方案 A 的正常路径)`lastKnownUserId` 是 null —— 不清，
       *     所以草稿留得住，用户点一下发送就能发出去。
       */
      const switchedAccount = lastKnownUserId !== null && lastKnownUserId !== result.user.userId
      lastKnownUserId = result.user.userId

      set({ user: result.user, loginOpen: false })
      if (switchedAccount) accountChangedHandler?.()

      const pending = get().pending
      set({ pending: null })
      // 原地恢复。⚠ 发送拦截那个入口挂的动作是「把焦点还给输入框」，**不是自动发送**
      await pending?.run()
    },

    async logout() {
      await api.auth.logout()
      queryClient.clear()
      lastKnownUserId = null
      set({ user: null, pending: null, loginOpen: false })
      // ⚠ 方案 A：登出就是未登录，**不再领匿名令牌**(方案 B 时代这里会 boot 一次)。
      // 清屏 + 清草稿 + 换会话 id(FE-8) —— 理由见上面 switchedAccount 那段。
      accountChangedHandler?.()
    },

    async updateNickname(nickname) {
      set({ user: await api.auth.updateMe({ nickname }) })
    },

    async setPassword(input) {
      await api.auth.setPassword(input)
    },
  }
})
